// Single source of truth for site content. Keep in sync with the CV.

export const contact = {
  email: "iburaksag@gmail.com",
  phoneDisplay: "+90 531 664 24 30",
  whatsapp: "https://wa.me/905316642430",
  linkedin: "https://www.linkedin.com/in/burak-sa%C4%9F-4883a2139/",
  github: "https://github.com/iburaksag",
};

export const headline = "Full Stack Software Engineer";

export const summary = [
  "Full Stack Software Engineer with 4 years of experience designing and delivering web applications across C#/.NET, PHP, JavaScript/TypeScript, and Python. Most recently built enterprise applications used daily by production, engineering, and management teams in a manufacturing environment, improving operational efficiency and delivering measurable cost savings.",
  "Brings a quality-focused mindset from hands-on QA and test automation experience, along with international experience from studying and working in Dublin, Ireland, including an MSc in Computer Science. Comfortable across the full stack, from backend services and data integrations to modern frontends, with a strong interest in clean architecture. Uses AI-assisted development to ship faster, while keeping the focus on understanding the problem, code quality, and maintainability.",
];

export const aboutStats = [
  { key: "experience", title: "Experience", value: "4+ Years" },
  { key: "projects", title: "Projects", value: "20+ Completed" },
  { key: "education", title: "Education", value: "MSc in Computer Science" },
];

// Highlighted on top of the Skills section (names must exist in SKILL_ICONS in Skills.jsx)
export const coreStack = ["C#", ".NET", "PHP", "React", "TypeScript", "Python", "MySQL", "SQL Server"];

export const skillGroups = [
  { title: "Languages", items: ["C#", "PHP", "JavaScript", "TypeScript", "Python", "SQL"] },
  {
    title: "Backend",
    items: [".NET Core", "ASP.NET Core", "ASP.NET MVC", "Web API", "Minimal API", "Entity Framework", "ADO.NET", "LINQ", "RESTful APIs", "JWT", "Node.js", "Express.js", "Django", "Flask"],
  },
  { title: "Frontend", items: ["React", "Vite", "Angular", "React Native", "Nx", "AG Grid", "HTML", "CSS", "jQuery", "Bootstrap"] },
  { title: "Databases", items: ["MySQL / MariaDB", "SQL Server", "PostgreSQL", "MongoDB", "Redis", "Snowflake", "Firebase"] },
  { title: "Architecture & Practices", items: ["SOLID", "Clean Architecture", "Layered Architecture", "CQRS", "Repository & Unit of Work", "Agile / Scrum"] },
  {
    title: "Tools & Platforms",
    items: ["Git", "GitLab CI/CD", "Google Cloud Platform", "SAP Integration", "Power BI", "Serilog", "FluentValidation", "NUnit", "Selenium"],
  },
];

export const experiences = [
  {
    company: "Siemens",
    role: "Full Stack Software Engineer",
    location: "Istanbul, Türkiye",
    period: "Feb 2024 – Present",
    current: true,
    bullets: [
      "Built and maintained backend-heavy applications on an enterprise MES platform using C#/.NET and PHP, applying SOLID and layered architecture principles within an Agile team.",
      "Led development of an engineering automation application for configure-to-order switchgear panels, reducing mechanical engineering time by 53–90%.",
      "Led the end-to-end development of a supplier order tracking system covering planning, supplier assignment, pallet and transport cage tracking within the factory, and shipment recording.",
      "Built React/TypeScript/Vite SPAs in an Nx monorepo using an enterprise design system and AG Grid, backed by RESTful APIs on MySQL/MariaDB; maintained legacy jQuery applications.",
      "Developed scheduled sync jobs replicating SAP data from Snowflake into MySQL, integrated SAP REST web services over mTLS, and built Python cronjobs ingesting data from JSON and text sources.",
      "Maintained GitLab CI/CD pipelines for deployments across development and production environments.",
      "Contributed to an internal AI agent for factory operations by authoring its domain knowledge and SQL skill definitions; wrote team guidelines for AI-assisted coding workflows.",
      "Built Power BI dashboards to visualize application data and operational metrics.",
    ],
    tech: ["C#", ".NET Core", "PHP", "React", "TypeScript", "Vite", "Python", "MySQL/MariaDB", "REST APIs", "GitLab CI/CD", "Nx", "SAP", "Snowflake", "Power BI"],
  },
  {
    company: "Keywords Studios",
    role: "Localization QA Tester",
    location: "Dublin, Ireland",
    period: "Jul 2022 – Jul 2023",
    bullets: [
      "Tested games for language, implementation, UI, and compliance issues; identified, recorded, and suggested fixes for defects.",
      "Wrote test cases, performed functional and localization testing, and reported issues in JIRA; owned daily reporting and bug vetting.",
    ],
    tech: ["Functional Testing", "Localization QA", "Test Cases", "JIRA"],
  },
  {
    company: "Final Group",
    role: "Full Stack Developer",
    location: "Istanbul, Türkiye",
    period: "Jul 2020 – Sep 2021",
    bullets: [
      "Developed 5+ data-centric web applications using C#, ASP.NET MVC, ASP.NET Core, Web Forms, LINQ, and SQL Server.",
      "Designed and developed C# business logic components in a 3-tier architecture interacting with SQL Server via Entity Framework and ADO.NET.",
      "Designed, developed, and tested web forms and RESTful web services; built frontends with AngularJS, jQuery, AJAX, and Bootstrap.",
    ],
    tech: ["C#", "ASP.NET MVC", "ASP.NET Core", "Entity Framework", "ADO.NET", "LINQ", "SQL Server", "AngularJS", "jQuery"],
  },
  {
    company: "Siemens",
    role: "Software Test Engineer Intern",
    location: "Istanbul, Türkiye",
    period: "Jul 2019 – Aug 2019",
    bullets: [
      "Built test automation with Selenium WebDriver and C# for PLC-connected web servers; wrote unit tests with NUnit.",
      "Created virtual PLCs in TIA Portal for test operations and performed manual web server testing within a Scrum team.",
    ],
    tech: ["C#", "Selenium", "NUnit", "TIA Portal", "Scrum"],
  },
  {
    company: "Ada Yazılım",
    role: "Software Developer Intern",
    location: "Istanbul, Türkiye",
    period: "Jul 2018 – Aug 2018",
    bullets: ["Developed Windows Forms applications in C# with SQL Server, T-SQL, and ADO.NET."],
    tech: ["C#", "Windows Forms", "SQL Server", "T-SQL", "ADO.NET"],
  },
];

// logo: key into the logo map in Educations.jsx (null = generic icon)
export const educations = [
  {
    degree: "MSc in Computer Science",
    school: "Griffith College Dublin",
    location: "Dublin, Ireland",
    period: "2021 – 2023",
    logo: "gcd",
    note: "Cloud computing, information retrieval, communication security and research methods. Dissertation: full stack car rental platform (MERN).",
  },
  {
    degree: "BSc in Computer Engineering",
    school: "Doğuş University",
    location: "Istanbul, Türkiye",
    period: "2015 – 2020",
    logo: "dogus",
    note: "Program taught 100% in English. Active member of student clubs, including the Industrial Engineering Club board.",
  },
  {
    degree: "Erasmus+ Exchange, Computer Engineering",
    school: "Lublin University of Technology",
    location: "Lublin, Poland",
    period: "2019 – 2020",
    logo: null,
    note: "Erasmus+ student exchange program in computer engineering.",
  },
];

export const certifications = [
  { title: ".NET Expertise Certification Programme", issuer: "BilgeAdam Academy" },
  { title: "Succeeding in Web Development: Full Stack and Front End", issuer: "" },
];
